import { createMerchantUser, getMerchantUsers, updateMerchantUser } from 'APIs/merchant.users/merchant.users';
import { CreateMerchantUserRequest, MerchantUser } from 'APIs/merchant.users/merchant.users.types';
import { Content, LpBox } from 'Constants/styles';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import getApiErrorMessage from 'utils/getApiErrorMsg';

import UserList from './UserList';
import { UserFormProps, toCreateMerchantUserRequest, toUpdateMerchantUserRequest } from './types';

const MerchantUsers = () => {
  const { merchantId } = useParams<{ merchantId: string; manage: string }>();
  const { apiBaseUri } = useSelector((state: ReduxStateType) => ({
    apiBaseUri: state.apiBaseUri,
  }));

  const [actionUsers, setActionUsers] = useState<Array<string>>([]);
  const [users, setUsers] = useState<Array<MerchantUser>>([]);
  const [fetchingError, setFetchingError] = useState<string>('');
  const [merchantUser, setMerchantUser] = useState<MerchantUser | null>(null);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [isModalLoading, setIsModalLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [modalErrorMsg, setModalErrorMsg] = useState<string>('');

  const getUser = useCallback(
    (userId: string): MerchantUser | null => {
      if (users.length === 0) return null;
      const userToEdit = users.filter((user) => user._id === userId);

      if (userToEdit.length === 0) return null;
      return userToEdit[0];
    },
    [users],
  );

  const fetchUsers = useCallback(async () => {
    if (!apiBaseUri) {
      return;
    }

    try {
      setIsLoading(true);
      const resp = await getMerchantUsers(apiBaseUri, merchantId);
      setUsers(resp);
    } catch (e) {
      setUsers([]);
      const errMsg = getApiErrorMessage(e);
      toast.error(`Error: ${errMsg}`);
      setFetchingError(errMsg);
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUri, merchantId]);

  const resendEmail = useCallback(
    async (userId: string) => {
      if (!apiBaseUri) {
        return;
      }

      const mUser = getUser(userId);
      if (mUser) {
        const confimed = window.confirm(
          `Are you sure you want to resend the onboarding email to ${mUser.emailAddress}?`,
        );
        if (!confimed) return;

        setActionUsers((prev) => [...prev, userId]);
        const payload: CreateMerchantUserRequest = {
          emailAddress: mUser.emailAddress,
          merchantId: mUser.merchantId,
          merchantUserRole: mUser.merchantUserRole,
          sendOnboardingEmail: true,
        };

        try {
          await createMerchantUser(apiBaseUri, payload);
          toast.success('Resent the onboarding email successfully');
        } catch (error) {
          toast.error(`Error: ${getApiErrorMessage(error)}`);
        } finally {
          setActionUsers((prev) => prev.filter((uId) => uId !== userId));
        }
      }
    },
    [apiBaseUri, getUser],
  );

  const saveMerchantUserHandler = useCallback(
    async (formData: UserFormProps) => {
      if (!apiBaseUri) {
        return;
      }

      try {
        setIsModalLoading(true);
        setModalErrorMsg('');
        if (formData.userId) {
          const updateUser = toUpdateMerchantUserRequest(merchantId, formData);
          await updateMerchantUser(apiBaseUri, updateUser);
          toast.success('User updated successfully');
        } else {
          const createUser = toCreateMerchantUserRequest(merchantId, formData);
          await createMerchantUser(apiBaseUri, createUser);
          toast.success('User created successfully');
        }
        setShowModal(false);
        fetchUsers();
      } catch (error) {
        setModalErrorMsg(getApiErrorMessage(error));
      } finally {
        setIsModalLoading(false);
      }
    },
    [apiBaseUri, fetchUsers, merchantId],
  );

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return (
    <>
      <Content className="merchant-invoices-content">
        <LpBox>
          <UserList
            actionUsers={actionUsers}
            users={users}
            fetchingError={fetchingError}
            merchantUser={merchantUser}
            showModal={showModal}
            setShowModal={setShowModal}
            isModalLoading={isModalLoading}
            isLoading={isLoading}
            modalErrorMsg={modalErrorMsg}
            setModalErrorMsg={setModalErrorMsg}
            setMerchantUser={setMerchantUser}
            saveMerchantUserHandler={saveMerchantUserHandler}
            resendEmail={resendEmail}
          />
        </LpBox>
      </Content>
    </>
  );
};

export default MerchantUsers;
